import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation.ts';
import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { BadStatusError } from '@confluence/network';

import type {
	PublishMutation as PublishMutationData,
	PublishMutationVariables,
} from './__types__/PublishMutation.ts';

interface PublishEditorContentBlueprintParsedResponse {
	status: number;
	traceId: string;
	success: boolean;
	createdDate: string | null;
	message: string | null;
	spaceName: string | null;
}

/**
 * Hook for publishing editor content via the blueprint REST API.
 * This is used when isBlueprintCreate is true, hitting the endpoint:
 * /wiki/rest/api/content/blueprint/instance/{contentId}?status=draft
 */
export const useExperimentalPublishEditorContentBlueprintMutation = () => {
	return useExperimentalMutation<
		PublishMutationData,
		PublishMutationVariables,
		PublishEditorContentBlueprintParsedResponse
	>({
		url: (variables) =>
			`/wiki/rest/api/content/blueprint/instance/${variables.contentId}?status=draft`,
		method: 'PUT',
		headers: {
			Accept: MediaType.JSON,
			'Content-Type': MediaType.JSON,
		},
		body: (variables) => {
			let history;
			if (variables.contentType === 'blogpost') {
				history = { createdDate: new Date().toISOString() };
			}

			const body = {
				id: variables.contentId,
				type: variables.contentType,
				title: variables.title,
				space: { key: variables.spaceKey },
				body: {
					atlas_doc_format: {
						value: variables.content,
						representation: 'atlas_doc_format',
						content: {
							id: variables.contentId,
						},
					},
				},
				status: 'current',
				version: {
					number: variables.version,
					minorEdit: variables.suppressNotifyWatchers,
					message: variables.message,
					...(variables.ncsStepVersion !== undefined && {
						ncsStepVersion: variables.ncsStepVersion,
					}),
				},
				extensions: {
					'update-trigger': 'EDIT_PAGE',
					moveRequest:
						Object.keys(variables.moveRequest ?? {}).length === 0
							? undefined
							: variables.moveRequest,
				},
				history,
				schedulePublishDate: variables.schedulePublishDate,
				ancestors: variables.ancestors,
				// Instead of sending restrictions: null, do not send restrictions at all
				restrictions: variables.restrictions ?? undefined,
			};

			return JSON.stringify(body);
		},
		parseResponseOnSuccess: async (response) => {
			const json = await response.json();
			return {
				status: response.status,
				traceId: response.headers.get('atl-traceid') ?? '',
				success: true,
				createdDate: json?.history?.createdDate ?? null,
				message: null,
				spaceName: json?.space?.name ?? null,
			};
		},
		parseResponseOnError: async (error) => {
			if (BadStatusError.isBadStatusError(error)) {
				const { response } = error;
				let json: { message?: string } = {};
				try {
					json = await response.json();
				} catch {
					// Ignore JSON parse errors
				}
				return {
					status: response.status,
					traceId: response.headers.get('atl-traceid') ?? '',
					success: false,
					createdDate: null,
					message: json?.message ?? null,
					spaceName: '',
				};
			}
			// Re-throw non-BadStatusError errors
			throw error;
		},
		parseResult: (parsedResponse) => {
			return {
				experimentalPublishEditorContent: {
					status: parsedResponse.status,
					success: parsedResponse.success,
					createdDate: parsedResponse.createdDate,
					message: parsedResponse.message,
					traceId: parsedResponse.traceId,
					space: {
						name: parsedResponse.spaceName,
					},
				},
			};
		},
	});
};
